import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { X } from 'lucide-react';
import { toast } from 'sonner';

const EditProjectModal = ({ isOpen, onClose, project, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    required_skills: [],
    required_certifications: [],
    other_certifications: '',
    number_of_consultants: 1,
    languages_required: [],
    currency: 'EUR',
    price_amount: 0,
    price_type: 'daily',
    duration_value: '',
    duration_unit: 'days',
    occupation_rate: 100,
    equipment_provided: false,
    remote_possible: false,
    remote_percentage: 0,
    remote_zone: '',
    project_type: '',
    commercial_model: '',
    city: '',
    country: '',
    start_date: ''
  });

  const [skillInput, setSkillInput] = useState('');
  const [certInput, setCertInput] = useState('');

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title || '',
        description: project.description || '',
        required_skills: project.required_skills || [],
        required_certifications: project.required_certifications || [],
        other_certifications: project.other_certifications || '',
        number_of_consultants: project.number_of_consultants || 1,
        languages_required: project.languages_required || [],
        currency: project.currency || 'EUR',
        price_amount: project.price_amount || 0,
        price_type: project.price_type || 'daily',
        duration_value: project.duration_value || '',
        duration_unit: project.duration_unit || 'days',
        occupation_rate: project.occupation_rate || 100,
        equipment_provided: project.equipment_provided || false,
        remote_possible: project.remote_possible || false,
        remote_percentage: project.remote_percentage || 0,
        remote_zone: project.remote_zone || '',
        project_type: project.project_type || '',
        commercial_model: project.commercial_model || '',
        city: project.city || '',
        country: project.country || '',
        start_date: project.start_date ? project.start_date.split('T')[0] : ''
      });
    }
  }, [project]);

  const addSkill = () => {
    if (skillInput.trim()) {
      setFormData({
        ...formData,
        required_skills: [...formData.required_skills, skillInput.trim()]
      });
      setSkillInput('');
    }
  };

  const removeSkill = (index) => {
    setFormData({
      ...formData,
      required_skills: formData.required_skills.filter((_, i) => i !== index)
    });
  };

  const addCertification = () => {
    if (certInput.trim()) {
      setFormData({
        ...formData,
        required_certifications: [...formData.required_certifications, certInput.trim()]
      });
      setCertInput('');
    }
  };

  const removeCertification = (index) => {
    setFormData({
      ...formData,
      required_certifications: formData.required_certifications.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.description) {
      toast.error('Title and description are required');
      return;
    }

    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      // Error handled by parent
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Project</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Basic Information</h3>
            
            <div>
              <Label htmlFor="title">Project Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="project_type">Project Type</Label>
                <select
                  id="project_type"
                  value={formData.project_type}
                  onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select type</option>
                  <option value="Implementation">Implementation</option>
                  <option value="Consulting">Consulting</option>
                  <option value="Administration">Administration</option>
                  <option value="Support">Support</option>
                  <option value="Development">Development</option>
                </select>
              </div>

              <div>
                <Label htmlFor="commercial_model">Commercial Model</Label>
                <select
                  id="commercial_model"
                  value={formData.commercial_model}
                  onChange={(e) => setFormData({ ...formData, commercial_model: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select model</option>
                  <option value="Time & Material">Time & Material</option>
                  <option value="Fixed Price">Fixed Price</option>
                </select>
              </div>
            </div>
          </div>

          {/* Skills & Certifications */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Skills & Certifications</h3>
            
            <div>
              <Label>Required Skills</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  placeholder="Enter a skill and press Enter"
                />
                <Button type="button" onClick={addSkill}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.required_skills.map((skill, index) => (
                  <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center gap-2">
                    {skill}
                    <X className="w-4 h-4 cursor-pointer" onClick={() => removeSkill(index)} />
                  </span>
                ))}
              </div>
            </div>

            <div>
              <Label>Required Certifications</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={certInput}
                  onChange={(e) => setCertInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                  placeholder="Enter a certification and press Enter"
                />
                <Button type="button" onClick={addCertification}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.required_certifications.map((cert, index) => (
                  <span key={index} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm flex items-center gap-2">
                    {cert}
                    <X className="w-4 h-4 cursor-pointer" onClick={() => removeCertification(index)} />
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Team & Budget */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Team & Budget</h3>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="number_of_consultants">Number of Consultants</Label>
                <Input
                  id="number_of_consultants"
                  type="number"
                  min="1"
                  value={formData.number_of_consultants}
                  onChange={(e) => setFormData({ ...formData, number_of_consultants: parseInt(e.target.value) || 1 })}
                />
              </div>

              <div>
                <Label htmlFor="price_amount">Price Amount</Label>
                <Input
                  id="price_amount"
                  type="number"
                  step="0.01"
                  value={formData.price_amount}
                  onChange={(e) => setFormData({ ...formData, price_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label htmlFor="price_type">Price Type</Label>
                <select
                  id="price_type"
                  value={formData.price_type}
                  onChange={(e) => setFormData({ ...formData, price_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="monthly">Monthly</option>
                  <option value="fixed">Fixed Price</option>
                </select>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Location</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remote_possible"
                checked={formData.remote_possible}
                onChange={(e) => setFormData({ ...formData, remote_possible: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="remote_possible">Remote work possible</Label>
            </div>

            {formData.remote_possible && (
              <div>
                <Label htmlFor="remote_percentage">Remote Percentage</Label>
                <Input
                  id="remote_percentage"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.remote_percentage}
                  onChange={(e) => setFormData({ ...formData, remote_percentage: parseInt(e.target.value) || 0 })}
                />
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Timeline</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="duration_value">Duration</Label>
                <div className="flex gap-2">
                  <Input
                    id="duration_value"
                    type="number"
                    step="0.1"
                    value={formData.duration_value}
                    onChange={(e) => setFormData({ ...formData, duration_value: parseFloat(e.target.value) || '' })}
                    className="flex-1"
                  />
                  <select
                    value={formData.duration_unit}
                    onChange={(e) => setFormData({ ...formData, duration_unit: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" style={{ backgroundColor: '#62D84E' }}>
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditProjectModal;
